<template>
	<SuiDataTableRow :class="rowClass" :data-testid="testId">
		<SuiDataTableCell
			v-for="item in costList"
			:key="item.id"
			:align="item.align"
			:class="item.class"
		>
			<span>
				{{ item.data }}
			</span>
		</SuiDataTableCell>
	</SuiDataTableRow>
</template>

<script setup lang="ts">
import {
	SuiDataTableCell,
	SuiDataTableColumnAlign,
	SuiDataTableRow,
} from '@developers/scoro-ui';
import { computed, toRefs } from 'vue';
import { useFormatter } from '@/composables';
import { ProjectProfitTableColumnTypes } from './types';
import { useProjectProfitTableCost } from './useProjectProfitTableCost';

const props = defineProps<{
	rowClass: string;
	testId?: string;
	costBudgeted: number;
	costRecognized: number;
	costInvoiced: number;
	costForecasted: number;
	isCostBudgetSet: boolean;
	isAnyBudgetSet: boolean;
	canSeeRecognized: boolean;
	canSeeForecast: boolean;
	isRevenueTmModel: boolean;
}>();

const {
	rowClass,
	testId,
	costBudgeted,
	costRecognized,
	costInvoiced,
	costForecasted,
	isCostBudgetSet,
	isAnyBudgetSet,
	canSeeRecognized,
	canSeeForecast,
	isRevenueTmModel,
} = toRefs(props);

const { formatNumber } = useFormatter();

const {
	costToRecognize,
	costForecastedTotal,
	isCostRecognizedOverBudget,
	isCostForecastedOverBudget,
	isCostInvoicedOverBudget,
} = useProjectProfitTableCost({
	costBudgeted,
	costRecognized,
	costForecasted,
	costInvoiced,
});

const getValueStateClass = (isValueOverBudget: boolean) => {
	if (isValueOverBudget) {
		return 'project-profit-table-cost__negative';
	}
	return '';
};

const costList = computed(() => {
	return [
		{
			id: ProjectProfitTableColumnTypes.BUDGETED,
			data: formatNumber(costBudgeted.value),
			align: SuiDataTableColumnAlign.RIGHT,
		},
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.EARNED,
						data: formatNumber(costRecognized.value),
						class:
							isCostBudgetSet.value &&
							getValueStateClass(
								isCostRecognizedOverBudget.value
							),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
		...(canSeeRecognized.value
			? [
					{
						id: ProjectProfitTableColumnTypes.RECOGNIZED_TO_DATE,
						data: formatNumber(costRecognized.value),
						class:
							isCostBudgetSet.value &&
							getValueStateClass(
								isCostRecognizedOverBudget.value
							),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
		...(canSeeRecognized.value &&
		!isRevenueTmModel.value &&
		isAnyBudgetSet.value
			? [
					{
						id: ProjectProfitTableColumnTypes.TO_RECOGNIZE,
						data: isCostRecognizedOverBudget.value
							? undefined
							: formatNumber(costToRecognize.value),
					},
				]
			: []),
		{
			id: ProjectProfitTableColumnTypes.INVOICED_TO_DATE,
			data: formatNumber(costInvoiced.value),
			class:
				isCostBudgetSet.value &&
				getValueStateClass(isCostInvoicedOverBudget.value),
			align: SuiDataTableColumnAlign.RIGHT,
		},
		{
			id: ProjectProfitTableColumnTypes.TO_INVOICE,
		},
		...(canSeeForecast.value
			? [
					{
						id: ProjectProfitTableColumnTypes.FORECASTED,
						data: formatNumber(costForecastedTotal.value),
						class:
							isCostBudgetSet.value &&
							getValueStateClass(
								isCostForecastedOverBudget.value
							),
						align: SuiDataTableColumnAlign.RIGHT,
					},
				]
			: []),
	];
});
</script>

<style scoped lang="scss">
.sui-data-table-col:last-child {
	padding-right: 1.5rem !important;
}

.project-profit-table-cost {
	text-wrap: nowrap;

	&__regular-row {
		--sui-data-table-cell-padding: 2rem;

		line-height: 2.5rem;
	}

	&__narrow-row {
		--sui-data-table-cell-padding: 1.625rem 2rem;

		background-color: $neutral-10;
		color: $neutral-80;
		font-size: 1.5rem;
		font-weight: $scoro-fw-normal;
	}

	&__shadow {
		box-shadow: $scoro-box-shadow-line-upper-inside;
	}

	&__negative {
		color: $red-50;
	}
}
</style>
